import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { useApiKey } from '../context/ApiKeyContext';
import { sendStructuredMessage } from '../utils/anthropicService';
import { generateAvatarUrl } from '../utils/characterGenerator';
import { MOODS } from '../shared/prompts';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 90vh;
  max-width: 800px;
  width: 100%;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  background-color: #0d9488;
  color: white;
`;

const Avatar = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin-right: 1rem;
  background-color: white;
`;

const ProfileInfo = styled.div`
  flex-grow: 1;
`;

const Name = styled.h3`
  margin: 0;
  font-size: 1.2rem;
`;

const Info = styled.p`
  margin: 0;
  font-size: 0.9rem;
  opacity: 0.9;
`;

const GoalBar = styled.div`
  padding: 0.6rem 1rem;
  background-color: #f0fdfa;
  border-bottom: 1px solid #ccfbf1;
  color: #0f766e;
  font-size: 0.85rem;
  text-align: center;

  strong {
    font-weight: 600;
  }
`;

// Quiet cue that the reply clock is running. Deliberately not a countdown: a
// visible timer makes the user optimise against the bar instead of attending to
// the conversation. The italic silence notes are the feedback signal instead.
const RealisticHint = styled.span`
  color: #0f766e;
  opacity: 0.75;
  font-style: italic;
`;

// Shown once the conversation reaches its natural close, so the user can sit
// with the last thing that was said before moving on to feedback.
const ConclusionArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-top: 1px solid #eee;
  background-color: #fafafa;
`;

const ConclusionText = styled.div`
  font-size: 0.95rem;
  color: #555;
  text-align: center;
`;

const ContinueButton = styled.button`
  padding: 0.75rem 2rem;
  background-color: #0d9488;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0f766e;
  }
`;

// A silence the user let happen, rendered in the transcript instead of a bubble.
const SilenceNote = styled.div`
  align-self: center;
  font-size: 0.82rem;
  font-style: italic;
  color: #999;
  padding: 0.1rem 0.5rem;
`;

const ChatMessages = styled.div`
  flex-grow: 1;
  padding: 1rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: #f5f5f5;
`;

const MessageBubble = styled.div`
  max-width: 70%;
  padding: 0.8rem 1rem;
  border-radius: 18px;
  position: relative;
  word-wrap: break-word;
  white-space: pre-wrap;

  ${({ isUser }) => isUser ? `
    align-self: flex-end;
    background-color: #0d9488;
    color: white;
    border-bottom-right-radius: 4px;
  ` : `
    align-self: flex-start;
    background-color: white;
    color: #333;
    border-bottom-left-radius: 4px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  `}
`;

const typingBlink = keyframes`
  0%, 80%, 100% { opacity: 0.3; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-3px); }
`;

const TypingDots = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 0.1rem 0;

  span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: #999;
    animation: ${typingBlink} 1.2s infinite ease-in-out both;
  }

  span:nth-child(2) { animation-delay: 0.15s; }
  span:nth-child(3) { animation-delay: 0.3s; }
`;

const OpenerPrompt = styled.div`
  margin: auto 0 0.5rem;
  text-align: center;
  color: #999;
  font-size: 0.9rem;
`;

const OpenerSuggestions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: auto;
`;

const OpenerChip = styled.button`
  padding: 0.6rem 1rem;
  border: 1px solid #0d9488;
  background-color: white;
  color: #0d9488;
  border-radius: 20px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;

  &:hover {
    background-color: #f0fdfa;
  }
`;

const InputArea = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  border-top: 1px solid #eee;
  gap: 0.75rem;
`;

// Shown when a trial conversation hits its turn budget. The closing feedback
// request is still allowed, so ending the chat keeps working.
const LimitNotice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-top: 1px solid #eee;
  text-align: center;
  color: #0f766e;
  background-color: #f0fdfa;
  font-size: 0.95rem;
`;

const MessageInput = styled.textarea`
  flex-grow: 1;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
  line-height: 1.4;
  resize: none;
  overflow-y: hidden;
  box-sizing: border-box;
  min-height: 48px;
  max-height: 150px;
`;

const SendButton = styled.button`
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #0d9488;
  background-color: #0d9488;
  color: white;

  &:hover {
    background-color: #0f766e;
    border-color: #0f766e;
  }

  &:disabled {
    background-color: #ccc;
    border-color: #ccc;
    color: white;
    cursor: not-allowed;
  }
`;

const EndButton = styled.button`
  padding: 0.75rem 1.25rem;
  font-size: 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #ddd;
  background-color: white;
  color: #555;
  white-space: nowrap;

  &:hover {
    background-color: #f5f5f5;
    border-color: #ccc;
    color: #333;
  }
`;

const MoodDisplay = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  padding: 0.5rem;
  background-color: #f9f9f9;
  border-bottom: 1px solid #eee;
`;

const MoodIndicator = styled.span`
  display: inline-block;
  margin: 0.15rem 0.35rem;
  padding: 0.3rem 0.5rem;
  background-color: ${({ active }) => (active ? '#0d9488' : '#f0f0f0')};
  color: ${({ active }) => (active ? 'white' : '#666')};
  border-radius: 4px;
  font-size: 0.8rem;
  transition: all 0.2s;
`;

// Celebratory confetti (dev-only /confetti command), portaled to <body>.
const confettiFall = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(var(--fall-distance)); }
`;

const confettiSway = keyframes`
  from { transform: translateX(calc(var(--amp) * -1)); }
  to { transform: translateX(var(--amp)); }
`;

const confettiSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const ConfettiOverlay = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 9999;
`;

const ConfettiFaller = styled.div`
  position: absolute;
  animation: ${confettiFall} linear forwards;
`;

const ConfettiSwayer = styled.div`
  animation: ${confettiSway} ease-in-out infinite alternate;
`;

const ConfettiPiece = styled.div`
  width: 8px;
  height: 14px;
  border-radius: 2px;
  animation: ${confettiSpin} linear infinite;
`;

const CONFETTI_COLORS = ['#0d9488', '#2dd4bf', '#ffd54f', '#4fc3f7', '#81c784', '#ba68c8'];

const gaussian = () => {
  let u = 0;
  let v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
};

const Confetti = () => {
  const pieces = useMemo(
    () => Array.from({ length: 400 }, (_, id) => {
      const startAbove = 5 + Math.random() * 95;
      const fallDistance = startAbove + 110;
      const speed = 30 + Math.random() * 25;
      return {
        id,
        left: Math.random() * 100,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        startTop: -startAbove,
        fallDistance,
        fallDuration: fallDistance / speed,
        releaseDelay: Math.min(2, Math.max(0, 1 + gaussian() * 0.5)),
        swayDuration: 0.8 + Math.random() * 1.6,
        swayAmp: 15 + Math.random() * 45,
        spinDuration: 0.6 + Math.random() * 1.6,
        spinReverse: Math.random() < 0.5,
      };
    }),
    []
  );

  if (typeof document === 'undefined') return null;

  return createPortal(
    <ConfettiOverlay aria-hidden="true">
      {pieces.map(p => (
        <ConfettiFaller
          key={p.id}
          style={{
            left: `${p.left}%`,
            top: `${p.startTop}vh`,
            animationDuration: `${p.fallDuration}s`,
            animationDelay: `${p.releaseDelay}s`,
            '--fall-distance': `${p.fallDistance}vh`,
          }}
        >
          <ConfettiSwayer
            style={{
              animationDuration: `${p.swayDuration}s`,
              '--amp': `${p.swayAmp}px`,
            }}
          >
            <ConfettiPiece
              style={{
                backgroundColor: p.color,
                animationDuration: `${p.spinDuration}s`,
                animationDirection: p.spinReverse ? 'reverse' : 'normal',
              }}
            />
          </ConfettiSwayer>
        </ConfettiFaller>
      ))}
    </ConfettiOverlay>,
    document.body
  );
};

// The reply window in realistic mode is `reading grace + thinking window`.
//
// The grace exists because the clock starts the moment the other person's line
// appears, before the user has read a word of it. Timing from there would
// measure reading speed, not hesitation, and would fire on everyone every turn.
// The thinking window is the part that's actually about the user.
//
// In voice mode the grace goes to zero: speech is heard as it's delivered, so
// delivery time *is* the grace, and the thinking window stands alone.
const DEFAULT_THINK_SEC = 4; // scenario override: `thinkWindowSec`
const READING_MS_PER_WORD = 300; // ~200 words per minute

// Floor on the user's spoken turns before a model-reported ending is accepted.
// Guards against wrapping up while the user is still saying hello. The real work
// of keeping a scene alive is done by the scenario's complication, not by this.
const MIN_TURNS = 2;
// Ceiling on user turns. Three turns out, the character is nudged to wind down;
// at the cap the conversation is closed for them.
const SOFT_CAP = 18;
const WIND_DOWN_LEAD = 3;

// Escalating silences. `prompt` goes to the model; `note` is the user's only
// cue that a pause landed (there is deliberately no visible countdown), and the
// coach reads it afterwards.
const SILENCE_TURNS = [
  {
    prompt: '[The user says nothing, letting the pause hang.]',
    note: 'You let the pause hang.',
  },
  {
    prompt: '[The user is still not saying anything. The silence is getting awkward.]',
    note: 'You still said nothing. The silence is getting awkward.',
  },
  {
    prompt: '[The user has now said nothing for a long, conspicuous stretch.]',
    note: 'You stayed silent for a long, conspicuous stretch.',
  },
];

// Rough proxy for how much there is to respond to in a message.
const topicComplexity = (text) => {
  const words = (text.trim().match(/\S+/g) || []).length;
  const questions = (text.match(/\?/g) || []).length;
  const denseWords = (text.match(/[A-Za-z]{8,}/g) || []).length;
  return words + questions * 6 + denseWords * 2;
};

const clampMs = (min, max, v) => Math.max(min, Math.min(max, v));

// Delay before the other person starts replying: ~2s for a throwaway line, up to
// 8s for a dense one. This is the window in which follow-up messages fold in.
const responseDelayMs = (text) => clampMs(2000, 8000, 1800 + topicComplexity(text) * 120);

// Time to read what the other person just said, before any hesitation counts.
const readingGraceMs = (text) => {
  const words = ((text || '').trim().match(/\S+/g) || []).length;
  return clampMs(1200, 8000, 400 + words * READING_MS_PER_WORD);
};

const CONFETTI_COMMAND = '/confetti';
const isLocalhost = () =>
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

const ChatInterface = ({ scenario, character, onEndChat, onTrialBlocked, conversationId, realistic = false }) => {
  const { apiKey, trialMode } = useApiKey();
  const auth = trialMode ? { trial: true } : { apiKey };

  // Time pressure only makes sense when you're speaking face to face. Taking a
  // few minutes to answer a text is ordinary behaviour, not hesitation.
  const pressureEnabled = realistic && scenario.medium === 'spoken';
  const thinkMs = (scenario.thinkWindowSec || DEFAULT_THINK_SEC) * 1000;

  // Seed the chat with the other person's opening line, if the scenario has one.
  const initialMessages = useMemo(
    () => (scenario.opening ? [{ role: 'assistant', content: scenario.opening }] : []),
    [scenario.opening]
  );

  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState('');
  const [isResponding, setIsResponding] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [currentMood, setCurrentMood] = useState('neutral');
  const [confettiBurst, setConfettiBurst] = useState(0);
  // Trial conversations have a server-side turn budget; once spent, the chat
  // stops accepting new messages but can still be ended for feedback.
  const [limitReached, setLimitReached] = useState(false);
  // Set once the conversation reaches its natural close. Holds the final
  // messages and how it ended, and pauses on the last line so the user can read
  // it before moving to feedback.
  const [conclusion, setConclusion] = useState(null);
  const silenceCountRef = useRef(0);
  // Realistic mode's hidden reply clock. `remainingRef` is the unspent budget in
  // ms; it is banked (not reset) when the user starts composing, so typing and
  // deleting a character doesn't refund the time they spent deliberating.
  const remainingRef = useRef(null);
  const startedAtRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const windowKeyRef = useRef(-1);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const endedRef = useRef(false);
  const typingTimeoutRef = useRef(null);
  const confettiTimeoutRef = useRef(null);
  const messagesRef = useRef(initialMessages);
  const pendingRef = useRef([]);
  const debounceRef = useRef(null);
  const processingRef = useRef(false);

  useEffect(() => () => {
    clearTimeout(typingTimeoutRef.current);
    clearTimeout(confettiTimeoutRef.current);
    clearTimeout(debounceRef.current);
    clearTimeout(silenceTimerRef.current);
  }, []);

  const fireConfetti = () => {
    clearTimeout(confettiTimeoutRef.current);
    setConfettiBurst(n => n + 1);
    confettiTimeoutRef.current = setTimeout(() => setConfettiBurst(0), 9500);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    if (!isResponding) {
      inputRef.current?.focus();
    }
  }, [isResponding]);

  // Grow the message box to fit its content; scrollbar only past the max height.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxHeight = 150;
    const style = window.getComputedStyle(el);
    const borderY = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    const fullHeight = el.scrollHeight + borderY;
    if (fullHeight > maxHeight) {
      el.style.height = `${maxHeight}px`;
      el.style.overflowY = 'auto';
    } else {
      el.style.height = `${fullHeight}px`;
      el.style.overflowY = 'hidden';
    }
  }, [input]);

  // The user's own turns. Silences are real turns to the model and count toward
  // the soft cap, but they aren't the user speaking, so they don't earn the
  // right to an ending.
  const countTurns = (bubbles) => {
    const all = bubbles.filter((m) => m.role === 'user');
    return { spoken: all.filter((m) => m.kind !== 'silence').length, total: all.length };
  };

  const pushMessage = (msg) => {
    const next = [...messagesRef.current, msg];
    messagesRef.current = next;
    setMessages(next);
    return next;
  };

  // The scene has reached its close. Pause on the final line with a button
  // through to feedback, rather than teleporting the user away from it.
  const concludeChat = (finalMessages, endReason, outcome) => {
    if (endedRef.current) return;
    endedRef.current = true;
    clearTimeout(debounceRef.current);
    clearTimeout(silenceTimerRef.current);
    pendingRef.current = [];
    setConclusion({ finalMessages, endReason, outcome });
  };

  // Move on once the user has read the last thing that was said to them.
  const handleContinue = () => {
    if (onEndChat) onEndChat(conclusion.finalMessages, conclusion);
  };

  const wait = (ms) => new Promise((resolve) => {
    typingTimeoutRef.current = setTimeout(resolve, ms);
  });

  const playTypingIndicator = async (reply) => {
    const totalDelay = Math.min(4500, 700 + reply.length * 25);
    setIsTyping(true);
    await wait(totalDelay);
    setIsTyping(false);
  };

  // Once the debounce window closes, send everything the user piled up as one
  // turn and reveal the reply.
  const flushPending = async () => {
    clearTimeout(debounceRef.current);
    const batch = pendingRef.current;
    if (batch.length === 0 || processingRef.current || endedRef.current) return;
    pendingRef.current = [];

    processingRef.current = true;
    setIsResponding(true);
    try {
      const turns = countTurns(messagesRef.current);
      const softCap = scenario.softCap || SOFT_CAP;
      const minTurns = scenario.minTurns || MIN_TURNS;
      const windingDown = turns.total >= softCap - WIND_DOWN_LEAD;

      const response = await sendStructuredMessage(
        auth,
        scenario,
        character,
        messagesRef.current,
        { conversationId, pressure: pressureEnabled, windDown: windingDown }
      );

      if (!response.success && (response.code === 'trial_exhausted' || response.code === 'owner_out_of_credits')) {
        if (onTrialBlocked) onTrialBlocked(response.code);
        return;
      }

      // Conversation ran its full turn budget: stop taking new messages, but
      // leave the door open to end the chat and read the coaching feedback.
      if (!response.success && response.code === 'turn_limit') {
        pendingRef.current = [];
        setLimitReached(true);
        return;
      }

      if (endedRef.current) return;

      if (response.success) {
        await playTypingIndicator(response.reply);
        if (endedRef.current) return;
        const updated = pushMessage({ role: 'assistant', content: response.reply });
        setCurrentMood(response.mood);

        // The model may declare an ending. Ignore it while the user has barely
        // spoken: a cooperative character will otherwise wrap up on turn one,
        // before the scenario's complication has had any chance to surface.
        if (response.ended && turns.spoken >= minTurns) {
          concludeChat(updated, response.endReason, response.outcome);
        } else if (turns.total >= softCap) {
          // It never took the wind-down hint. Close it for them.
          concludeChat(updated, 'The conversation ran its course.', 'unresolved');
        }
      } else {
        console.error('Error sending message:', response.error);
        pushMessage({
          role: 'assistant',
          content: "Sorry, I'm having trouble responding right now. Can we pick this back up?",
        });
      }
    } catch (error) {
      console.error('Error in flushPending:', error);
    } finally {
      processingRef.current = false;
      setIsResponding(false);
      if (pendingRef.current.length > 0) scheduleFlush();
    }
  };

  const scheduleFlush = () => {
    if (processingRef.current) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(flushPending, responseDelayMs(pendingRef.current.join('\n')));
  };

  // The reply window ran out. The silence itself becomes the user's turn: the
  // character is told about it and reacts, rather than waiting indefinitely.
  // Sent immediately (no debounce) since there's nothing more to batch.
  const handleSilence = () => {
    if (endedRef.current || processingRef.current || limitReached) return;
    const index = Math.min(silenceCountRef.current, SILENCE_TURNS.length - 1);
    silenceCountRef.current += 1;
    const { prompt, note } = SILENCE_TURNS[index];

    pushMessage({ role: 'user', kind: 'silence', content: prompt, note });
    pendingRef.current.push(prompt);
    clearTimeout(debounceRef.current);
    flushPending();
  };

  // The reply clock runs only when the floor is genuinely the user's, and pauses
  // while they're composing, so it times hesitation rather than typing speed.
  const lastIsAssistant =
    messages.length > 0 && messages[messages.length - 1].role === 'assistant';
  const lastAssistantText = lastIsAssistant ? messages[messages.length - 1].content : '';
  const clockRunning =
    pressureEnabled && lastIsAssistant && !limitReached && !conclusion &&
    !isResponding && !isTyping && !input.trim();

  useEffect(() => {
    // Paused or stopped: bank whatever time was spent, don't discard it.
    if (!clockRunning) {
      if (startedAtRef.current !== null) {
        const elapsed = Date.now() - startedAtRef.current;
        remainingRef.current = Math.max(0, (remainingRef.current || 0) - elapsed);
        startedAtRef.current = null;
      }
      clearTimeout(silenceTimerRef.current);
      return;
    }

    // A new line from the other person starts a fresh budget: time to read it,
    // then time to think. Resuming after a pause keeps the unspent remainder.
    if (windowKeyRef.current !== messages.length) {
      windowKeyRef.current = messages.length;
      remainingRef.current = readingGraceMs(lastAssistantText) + thinkMs;
    }

    startedAtRef.current = Date.now();
    silenceTimerRef.current = setTimeout(() => {
      startedAtRef.current = null;
      remainingRef.current = null;
      handleSilence();
    }, remainingRef.current);

    return () => clearTimeout(silenceTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clockRunning, thinkMs, messages.length, lastAssistantText]);

  const handleSendMessage = () => {
    if (!input.trim()) return;

    // Dev-only cheat: on localhost, "/confetti" fires the celebration.
    if (isLocalhost() && input.trim().toLowerCase() === CONFETTI_COMMAND) {
      setInput('');
      fireConfetti();
      return;
    }

    // Speaking up clears the escalating-silence streak.
    silenceCountRef.current = 0;
    pushMessage({ role: 'user', content: input });
    pendingRef.current.push(input);
    setInput('');
    scheduleFlush();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Ending by hand goes straight to feedback. It's a deliberate action, so
  // there's no last line to pause on, and no in-fiction reason it ended.
  const handleEndChat = () => {
    if (endedRef.current) return;
    endedRef.current = true;
    clearTimeout(debounceRef.current);
    clearTimeout(silenceTimerRef.current);
    if (onEndChat) onEndChat(messagesRef.current, null);
  };

  // Show opener suggestions until the user has sent their first message.
  const userHasSpoken = messages.some((m) => m.role === 'user');

  return (
    <Container>
      {confettiBurst > 0 && <Confetti key={confettiBurst} />}
      <Header>
        <Avatar src={generateAvatarUrl(character, currentMood)} alt={character.name} />
        <ProfileInfo>
          <Name>{character.name}</Name>
          <Info>{character.subtitle}</Info>
        </ProfileInfo>
      </Header>

      <GoalBar>
        <strong>Your goal:</strong> {scenario.goal}
        {pressureEnabled && <RealisticHint> Pauses are noticed.</RealisticHint>}
      </GoalBar>

      <MoodDisplay>
        {MOODS.map(mood => (
          <MoodIndicator key={mood} active={currentMood === mood}>
            {mood}
          </MoodIndicator>
        ))}
      </MoodDisplay>

      <ChatMessages>
        {messages.map((message, index) => (
          message.kind === 'silence' ? (
            <SilenceNote key={index}>{message.note}</SilenceNote>
          ) : (
            <MessageBubble key={index} isUser={message.role === 'user'}>
              {message.content}
            </MessageBubble>
          )
        ))}
        {isTyping && (
          <MessageBubble isUser={false}>
            <TypingDots aria-label={`${character.name} is typing`}>
              <span /><span /><span />
            </TypingDots>
          </MessageBubble>
        )}
        {!userHasSpoken && (
          <>
            <OpenerPrompt>Not sure how to start? Try one of these, or write your own below.</OpenerPrompt>
            <OpenerSuggestions>
              {scenario.openers.map((suggestion, index) => (
                <OpenerChip
                  key={index}
                  onClick={() => {
                    setInput(suggestion);
                    inputRef.current?.focus();
                  }}
                >
                  {suggestion}
                </OpenerChip>
              ))}
            </OpenerSuggestions>
          </>
        )}
        <div ref={messagesEndRef} />
      </ChatMessages>

      {conclusion ? (
        <ConclusionArea>
          <ConclusionText>
            {conclusion.endReason || 'The conversation came to an end.'}
          </ConclusionText>
          <ContinueButton onClick={handleContinue}>See feedback</ContinueButton>
        </ConclusionArea>
      ) : limitReached ? (
        <LimitNotice>
          <span>This practice conversation has reached its length limit.</span>
          <EndButton onClick={handleEndChat}>End &amp; get feedback</EndButton>
        </LimitNotice>
      ) : (
        <InputArea>
          <MessageInput
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={scenario.medium === 'text' ? 'Type your message...' : 'Type what you say...'}
            rows={1}
          />
          <SendButton onClick={handleSendMessage} disabled={!input.trim()}>
            Send
          </SendButton>
          <EndButton onClick={handleEndChat}>End</EndButton>
        </InputArea>
      )}
    </Container>
  );
};

export default ChatInterface;
